import React, { useMemo, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { toast } from "sonner";
import api from "../lib/api";

/**
 * Phase 5 · Get Help With This Order.
 *
 * Three-step flow:
 *   1. Which item (or whole order)?
 *   2. What's wrong?  (reason_code chip + optional free-text)
 *   3. Submit — hits POST /orders/{id}/report-issue which flips has_issue,
 *      appends a message in the order chat, and routes to the Vendor Order
 *      Center Issues lane.
 */

const REASONS = [
  { code: "wrong_item", label: "Wrong item", severity: "high" },
  { code: "missing_item", label: "Missing item", severity: "high" },
  { code: "allergy", label: "Allergy / safety", severity: "high" },
  { code: "quality", label: "Quality issue", severity: "normal" },
  { code: "late", label: "Running late", severity: "normal" },
  { code: "other", label: "Something else", severity: "normal" },
];

export default function GetHelpDialog({ order, open, onOpenChange, onSubmitted }) {
  const [lineId, setLineId] = useState("");
  const [reason, setReason] = useState("");
  const [details, setDetails] = useState("");
  const [busy, setBusy] = useState(false);

  const canSubmit = reason && details.trim().length >= 5;

  const lines = useMemo(() => (order?.items || []).filter((it) => it.line_id), [order]);

  const reset = () => {
    setLineId("");
    setReason("");
    setDetails("");
  };

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      await api.post(`/orders/${order.order_id}/report-issue`, {
        line_id: lineId || null,
        reason_code: reason,
        details: details.trim(),
      });
      toast.success("Issue sent to the shop");
      onSubmitted?.();
      reset();
      onOpenChange?.(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange?.(v); }}>
      <DialogContent className="bg-zinc-950 border-white/10 text-white max-w-md" data-testid="get-help-dialog">
        <DialogHeader>
          <DialogTitle>Get help with this order</DialogTitle>
          <DialogDescription className="text-zinc-500">
            {order?.vendor_name ? `We'll ping @${order.vendor_name}'s team right away.` : "We'll ping the vendor right away."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-1">
          {/* Which item */}
          <section>
            <h3 className="text-[11px] uppercase tracking-widest font-bold text-zinc-500 mb-2">Which item?</h3>
            <div className="space-y-1.5 max-h-40 overflow-y-auto pr-1">
              <button
                onClick={() => setLineId("")}
                className={`w-full text-left px-3 py-2 rounded-xl border text-sm ${
                  !lineId
                    ? "border-pink-500/60 bg-pink-500/10 text-white"
                    : "border-white/10 bg-white/[0.03] text-zinc-300"
                }`}
                data-testid="get-help-line-all"
              >
                The whole order
              </button>
              {lines.map((it) => (
                <button
                  key={it.line_id}
                  onClick={() => setLineId(it.line_id)}
                  className={`w-full text-left px-3 py-2 rounded-xl border text-sm ${
                    lineId === it.line_id
                      ? "border-pink-500/60 bg-pink-500/10 text-white"
                      : "border-white/10 bg-white/[0.03] text-zinc-300"
                  }`}
                  data-testid={`get-help-line-${it.line_id}`}
                >
                  <div className="font-semibold truncate">× {it.quantity} {it.name}</div>
                  {it.customization_summary?.variation && (
                    <div className="text-[11px] text-zinc-500">Size: {it.customization_summary.variation}</div>
                  )}
                </button>
              ))}
            </div>
          </section>

          {/* Reason */}
          <section>
            <h3 className="text-[11px] uppercase tracking-widest font-bold text-zinc-500 mb-2">What happened?</h3>
            <div className="flex flex-wrap gap-1.5">
              {REASONS.map((r) => (
                <button
                  key={r.code}
                  onClick={() => setReason(r.code)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
                    reason === r.code
                      ? "bg-pink-500 border-pink-400 text-white"
                      : "bg-white/5 border-white/10 text-zinc-300 hover:bg-white/10"
                  }`}
                  data-testid={`get-help-reason-${r.code}`}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </section>

          {/* Details */}
          <section>
            <h3 className="text-[11px] uppercase tracking-widest font-bold text-zinc-500 mb-2">Details</h3>
            <textarea
              value={details}
              onChange={(e) => setDetails(e.target.value.slice(0, 800))}
              rows={3}
              placeholder="E.g. I ordered extra pickles but there weren't any..."
              className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm placeholder:text-zinc-500 resize-none"
              data-testid="get-help-details"
            />
            <p className="text-[10px] text-zinc-500 mt-1 text-right">{details.length}/800</p>
          </section>

          <div className="flex gap-2 justify-end pt-1">
            <Button onClick={() => onOpenChange?.(false)} variant="outline" className="rounded-full border-white/10 text-zinc-300">
              Not now
            </Button>
            <Button
              disabled={!canSubmit || busy}
              onClick={submit}
              className="rounded-full bg-pink-500 hover:bg-pink-400 text-white font-bold"
              data-testid="get-help-submit"
            >
              {busy ? "Sending…" : "Send to vendor"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
